// src/Login.js
import React, { useState, useEffect, useRef } from 'react';
import './Login.css';
import logo from '../logo.png';
import { safeSetItem } from './safeStorage';

function Login({ onLoginSuccess }) {
  const [username, setUsername]       = useState('');
  const [password, setPassword]       = useState('');
  const [rememberMe, setRememberMe]   = useState(false);
  const [error, setError]             = useState('');
  const [isLoading, setIsLoading]     = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const loginCardRef = useRef(null);
  const newsCardRef  = useRef(null);

  // APK modal
  const [showApkModal, setShowApkModal]   = useState(false);
  const [apkFiles, setApkFiles]           = useState([]);
  const [isLoadingApks, setIsLoadingApks] = useState(false);

  // News
  const [newsContent, setNewsContent]   = useState('');
  const [isLoadingNews, setIsLoadingNews] = useState(true);

  // PWA install
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [pwaInstalled, setPwaInstalled]     = useState(false);

  // ── Load remembered credentials ──────────────────────────────────────────
  useEffect(() => {
    try {
      const saved = localStorage.getItem('pm_remember');
      if (saved) {
        const { u, p } = JSON.parse(saved);
        if (u) setUsername(u);
        if (p) setPassword(p);
        setRememberMe(true);
      }
    } catch {}
  }, []);

  // ── PWA install prompt ───────────────────────────────────────────────────
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setPwaInstalled(true);
    }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setPwaInstalled(true); setDeferredPrompt(null); });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // ── News card height sync ────────────────────────────────────────────────
  useEffect(() => {
    const sync = () => {
      if (loginCardRef.current && newsCardRef.current)
        newsCardRef.current.style.minHeight = loginCardRef.current.offsetHeight + 'px';
    };
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  useEffect(() => { fetchNews(); }, []);

  const fetchNews = async () => {
    try {
      setIsLoadingNews(true);
      const r = await fetch('/api/news');
      if (r.ok) { const d = await r.json(); setNewsContent(d.content || ''); }
    } catch {}
    finally { setIsLoadingNews(false); }
  };

  // ── Login ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Login failed');
      if (!data.token) throw new Error('Authentication token missing from server response');

      // Remember me
      if (rememberMe) {
        localStorage.setItem('pm_remember', JSON.stringify({ u: username, p: password }));
      } else {
        localStorage.removeItem('pm_remember');
      }

      safeSetItem('user', JSON.stringify(data.user));
      safeSetItem('token', data.token);
      onLoginSuccess({ ...data.user, webdavCredentials: null }, data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ── APK ──────────────────────────────────────────────────────────────────
  const handleDownloadAPK = async () => {
    try {
      setIsLoadingApks(true);
      setShowApkModal(true);
      const r = await fetch('/api/apk/list');
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to fetch APK list');
      setApkFiles(d.files || []);
    } catch (err) {
      setError('Failed to load APK files: ' + err.message);
      setShowApkModal(false);
    } finally {
      setIsLoadingApks(false);
    }
  };

  const closeApkModal   = () => { setShowApkModal(false); setApkFiles([]); };
  const downloadSingleApk = (f) => {
    const a = document.createElement('a');
    a.href = `/apk/${f}`; a.download = f;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // ── PWA Install ──────────────────────────────────────────────────────────
  const handleInstallPWA = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setDeferredPrompt(null);
    } else {
      // Fallback instructions
      const ua = navigator.userAgent;
      const isIOS = /iphone|ipad|ipod/i.test(ua);
      if (isIOS) {
        alert('On iPhone/iPad:\nTap the Share button (□↑) at the bottom of Safari → "Add to Home Screen"');
      } else {
        alert('In Chrome/Edge: open the browser menu (⋮) → "Install app" or "Add to Home Screen"');
      }
    }
  };

  // ── News parse ───────────────────────────────────────────────────────────
  const parseNews = (c) => !c ? [] : c.split('---').filter(x => x.trim()).map(x => {
    const lines = x.trim().split('\n');
    return { date: lines[0] || '', title: lines[1] || '', description: lines.slice(2).join('\n') };
  });
  const newsItems = parseNews(newsContent);

  return (
    <div className="login-page-container">
      <div className="login-container">

        {/* ── LOGIN CARD ── */}
        <div className="login-card" ref={loginCardRef}>
          {/* Animated top strip */}
          <div className="login-top-strip" />

          <div className="login-card-body">
            {/* Header */}
            <div className="login-header">
              <div className="login-logo-wrap">
                <img src={logo} alt="Promedia Logo" className="login-logo" />
              </div>
              <h1>Promedia Sign</h1>
              <p>Digital Signage Management Platform</p>
            </div>

            {error && (
              <div className="login-error">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input type="text" id="username" value={username}
                  onChange={e => setUsername(e.target.value)}
                  required autoComplete="username" placeholder="Enter your username" />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password" value={password}
                    onChange={e => setPassword(e.target.value)}
                    required autoComplete="current-password" placeholder="Enter your password"
                  />
                  <span className="password-toggle"
                    onClick={() => setShowPassword(v => !v)}
                    role="button" tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowPassword(v => !v); } }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    )}
                  </span>
                </div>
              </div>

              {/* Remember Me */}
              <div className="remember-row">
                <label className="remember-label">
                  <span className="remember-checkbox-wrap">
                    <input type="checkbox" checked={rememberMe}
                      onChange={e => setRememberMe(e.target.checked)} />
                    <span className="remember-box">
                      <svg viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="1.5,6 4.5,9 10.5,3"/>
                      </svg>
                    </span>
                  </span>
                  Remember me
                </label>
              </div>

              <button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? (
                  <svg className="spin-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/></svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                )}
                {isLoading ? 'Signing in…' : 'Login'}
              </button>
            </form>

            {/* Divider */}
            <div className="contact-divider"><span>CONTACT &amp; SUPPORT</span></div>

            {/* Contact grid */}
            <div className="contact-grid">
              <button type="button" className="btn-outline btn-help" onClick={() => window.open('https://promediadi.com/demo-studio/g/maestro-qxw8/s/xmu11vj8dngr', '_blank', 'noopener,noreferrer')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                Help
              </button>
              <a href="https://youtu.be/aurfDu7Kvj0" target="_blank" rel="noopener noreferrer" className="btn-outline btn-tutorial">
                <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                Tutorial
              </a>
              <a href="tel:+201288478470" className="btn-outline btn-call">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                Call Us
              </a>
              <a href="https://wa.me/201288478470" target="_blank" rel="noopener noreferrer" className="btn-outline btn-whatsapp">
                <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                WhatsApp
              </a>
              <a href="https://t.me/promediasignbot" target="_blank" rel="noopener noreferrer" className="btn-outline btn-telegram">
                <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                Telegram
              </a>
              <a href="https://www.facebook.com/promedia.di" target="_blank" rel="noopener noreferrer" className="btn-outline btn-facebook">
                <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </a>

              {/* APK row — two equal buttons */}
              <div className="btn-apk-row">
                <button type="button" className="btn-apk btn-apk-download" onClick={handleDownloadAPK}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Download APK
                </button>
                <button type="button"
                  className={`btn-apk btn-install-pwa${pwaInstalled ? ' installed' : ''}`}
                  onClick={handleInstallPWA}
                  title={pwaInstalled ? 'Already installed as app' : 'Install as desktop / home-screen app'}>
                  {pwaInstalled ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="3"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                  )}
                  {pwaInstalled ? 'App Installed ✓' : 'Install on Device'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── NEWS CARD ── */}
        {newsItems.length > 0 && (
          <div className="news-card" ref={newsCardRef}>
            <div className="news-header">
              <span className="news-icon">📰</span>
              <h3>Latest News &amp; Updates</h3>
              <span className="news-badge">NEW</span>
            </div>
            <div className="news-content">
              {isLoadingNews ? (
                <div className="news-loading">Loading news…</div>
              ) : (
                newsItems.map((item, i) => {
                  const isRTL = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/.test(item.title + item.description);
                  return (
                    <div key={i} className="news-item" dir={isRTL ? 'rtl' : 'ltr'}>
                      <div className="news-date">{item.date}</div>
                      <div className="news-title">{item.title}</div>
                      <div className="news-text">{item.description}</div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── APK MODAL ── */}
      {showApkModal && (
        <div className="modal-overlay" onClick={closeApkModal}>
          <div className="apk-modal" onClick={e => e.stopPropagation()}>
            <div className="apk-modal-header">
              <h2>Available APK Files</h2>
              <button className="close-button" onClick={closeApkModal}>×</button>
            </div>
            <div className="apk-modal-content">
              {isLoadingApks ? (
                <div className="loading-spinner">Loading APK files…</div>
              ) : apkFiles.length === 0 ? (
                <div className="no-files">No APK files available</div>
              ) : (
                <div className="apk-file-list">
                  {apkFiles.map((file, i) => (
                    <div key={i} className="apk-file-item">
                      <div className="apk-file-info">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        <span className="apk-filename">{file}</span>
                      </div>
                      <button className="apk-download-btn" onClick={() => downloadSingleApk(file)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="apk-modal-footer">
              <button className="close-modal-btn" onClick={closeApkModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
